/**
 * Strip the legacy `MIPS ` prefix from existing MIPS-section bullets in
 * a saved DraftJS template (string form). Walks blocks BETWEEN
 * `mipssdempty` and `text25` and rewrites `^MIPS\s+QPP...` → `QPP...`.
 *
 * The strip is scoped to `MIPS ` followed by `QPP` (lookahead) so a stray
 * bullet that legitimately starts with "MIPS …" but isn't a QPP row is
 * left alone. The `m` flag makes `^` match at each line start so two QPP
 * entries crammed into one block separated by '\n' both get stripped
 * (SplitListItemNewlines tears them into separate blocks later).
 *
 * Returns the template unchanged when JSON parsing fails or either anchor
 * block is missing. Idempotent: clean templates pass through unchanged.
 *
 * Mirrors the backend Helpers/MipsBullets.php::stripLegacyPrefix so that
 * old-FE renders match the AI-driven new-FE / backend PDF output.
 * Old FE has no AI MIPS apply path, but the underlying DB text_template
 * is shared across both FEs — so the cleanup still applies here.
 */
export function stripLegacyMipsPrefix(template) {
  if (!template) return template;
  try {
    const parsed = JSON.parse(template);
    if (!parsed?.blocks || !Array.isArray(parsed.blocks)) return template;

    const startIdx = parsed.blocks.findIndex((b) => b?.key === "mipssdempty");
    const endIdx = parsed.blocks.findIndex((b) => b?.key === "text25");
    if (startIdx === -1 || endIdx === -1 || endIdx <= startIdx) return template;

    let mutated = false;
    for (let i = startIdx + 1; i < endIdx; i++) {
      const block = parsed.blocks[i];
      if (!block || typeof block.text !== "string" || block.text === "") continue;
      const stripped = block.text.replace(/^MIPS\s+(?=QPP)/gim, "");
      if (stripped === block.text) continue;

      const removed = block.text.length - stripped.length;
      block.text = stripped;
      if (
        removed > 0 &&
        Array.isArray(block.inlineStyleRanges) &&
        block.inlineStyleRanges.length > 0
      ) {
        const newRanges = [];
        for (const r of block.inlineStyleRanges) {
          const offset = r?.offset ?? 0;
          const length = r?.length ?? 0;
          const end = offset + length;
          if (end <= removed) continue;
          const newOffset = Math.max(0, offset - removed);
          const newLength = end - Math.max(offset, removed);
          if (newLength > 0) newRanges.push({ ...r, offset: newOffset, length: newLength });
        }
        block.inlineStyleRanges = newRanges;
      }
      mutated = true;
    }

    return mutated ? JSON.stringify(parsed) : template;
  } catch {
    return template;
  }
}
